import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tabs,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Descriptions,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import { formatBrisbaneTime } from '../../utils/time';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface Alert {
  id: number;
  rule_id: number | null;
  alert_type: string;
  severity: string;
  status: string;
  title: string;
  message: string;
  details: any;
  store_id: number | null;
  resource_type: string | null;
  resource_id: number | null;
  email_sent: boolean;
  email_sent_at: string | null;
  acknowledged_by: number | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  created_at: string;
}

interface AlertRule {
  id: number;
  name: string;
  alert_type: string;
  severity: string;
  enabled: boolean;
  conditions: any;
  notify_email: boolean;
  notify_slack: boolean;
  email_recipients: string[] | null;
  cooldown_minutes: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const AlertsPage: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAlerts();
    fetchRules();
  }, [page, pageSize, statusFilter]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        page_size: pageSize,
      };
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await api.get('/admin/alerts', { params });
      setAlerts(response.data.items);
      setTotal(response.data.total);
    } catch (error: any) {
      message.error('Failed to fetch alerts: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const response = await api.get('/admin/alerts/rules');
      setRules(response.data);
    } catch (error: any) {
      message.error('Failed to fetch alert rules: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRulesLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      await api.post(`/admin/alerts/${alertId}/acknowledge`, {});
      message.success('Alert acknowledged');
      fetchAlerts();
    } catch (error: any) {
      message.error('Failed to acknowledge: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResolve = async (alertId: number, notes?: string) => {
    try {
      await api.post(`/admin/alerts/${alertId}/resolve`, {
        resolution_notes: notes,
      });
      message.success('Alert resolved');
      fetchAlerts();
    } catch (error: any) {
      message.error('Failed to resolve: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleViewDetail = (alert: Alert) => {
    setSelectedAlert(alert);
    setDetailModalVisible(true);
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      notify_email: true,
      notify_slack: false,
      cooldown_minutes: 60,
      severity: 'medium',
    });
    setRuleModalVisible(true);
  };

  const handleEditRule = (rule: AlertRule) => {
    setEditingRule(rule);
    form.setFieldsValue(rule);
    setRuleModalVisible(true);
  };

  const handleDeleteRule = async (ruleId: number) => {
    try {
      await api.delete(`/admin/alerts/rules/${ruleId}`);
      message.success('Rule deleted successfully');
      fetchRules();
    } catch (error: any) {
      message.error('Failed to delete rule: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRuleSubmit = async (values: any) => {
    try {
      if (editingRule) {
        await api.patch(`/admin/alerts/rules/${editingRule.id}`, values);
        message.success('Rule updated successfully');
      } else {
        await api.post('/admin/alerts/rules', values);
        message.success('Rule created successfully');
      }
      setRuleModalVisible(false);
      fetchRules();
    } catch (error: any) {
      message.error('Operation failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const response = await api.post('/admin/alerts/check', {});
      message.success(`Check completed, created ${response.data.alerts_created} alerts`);
      fetchAlerts();
    } catch (error: any) {
      message.error('Check failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'blue',
      medium: 'orange',
      high: 'red',
      critical: 'magenta',
    };
    return colors[severity] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'orange',
      sent: 'blue',
      acknowledged: 'cyan',
      resolved: 'green',
      failed: 'red',
    };
    return colors[status] || 'default';
  };

  const alertColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 150,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (text: string) => (
        <Tag color={getSeverityColor(text)}>{text.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string) => (
        <Tag color={getStatusColor(text)}>{text}</Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Store ID',
      dataIndex: 'store_id',
      key: 'store_id',
      width: 100,
      render: (text: number | null) => text || '-',
    },
    {
      title: 'Created At (Brisbane)',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => formatBrisbaneTime(text),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 200,
      render: (_: any, record: Alert) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            Details
          </Button>
          {record.status === 'sent' && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleAcknowledge(record.id)}
            >
              Acknowledge
            </Button>
          )}
          {record.status !== 'resolved' && (
            <Popconfirm
              title="Are you sure you want to resolve this alert?"
              onConfirm={() => {
                Modal.confirm({
                  title: 'Resolve Alert',
                  content: (
                    <TextArea
                      rows={4}
                      placeholder="Enter resolution notes (optional)"
                      onChange={(e) => {
                        // Store notes in a ref or state
                        (window as any).__resolveNotes = e.target.value;
                      }}
                    />
                  ),
                  onOk: () => {
                    const notes = (window as any).__resolveNotes || '';
                    handleResolve(record.id, notes);
                    delete (window as any).__resolveNotes;
                  },
                });
              }}
            >
              <Button type="link" icon={<CloseCircleOutlined />}>
                Resolve
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const ruleColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 150,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (text: string) => (
        <Tag color={getSeverityColor(text)}>{text.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (text: boolean) => (text ? 'Yes' : 'No'),
    },
    {
      title: 'Email Notify',
      dataIndex: 'notify_email',
      key: 'notify_email',
      width: 100,
      render: (text: boolean) => (text ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 150,
      render: (_: any, record: AlertRule) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditRule(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => handleDeleteRule(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Alert Management"
        extra={
          <Button
            type="primary"
            icon={<BellOutlined />}
            onClick={handleCheckAlerts}
          >
            Manual Check
          </Button>
        }
      >
        <Tabs defaultActiveKey="alerts">
          <TabPane tab="Alerts" key="alerts">
            <Space style={{ marginBottom: 16 }}>
              <Button
                onClick={() => setStatusFilter(undefined)}
                type={statusFilter === undefined ? 'primary' : 'default'}
              >
                All
              </Button>
              <Button
                onClick={() => setStatusFilter('pending')}
                type={statusFilter === 'pending' ? 'primary' : 'default'}
              >
                Pending
              </Button>
              <Button
                onClick={() => setStatusFilter('sent')}
                type={statusFilter === 'sent' ? 'primary' : 'default'}
              >
                Sent
              </Button>
              <Button
                onClick={() => setStatusFilter('acknowledged')}
                type={statusFilter === 'acknowledged' ? 'primary' : 'default'}
              >
                Acknowledged
              </Button>
              <Button
                onClick={() => setStatusFilter('resolved')}
                type={statusFilter === 'resolved' ? 'primary' : 'default'}
              >
                Resolved
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchAlerts}>
                Refresh
              </Button>
            </Space>

            <Table
              columns={alertColumns}
              dataSource={alerts}
              loading={loading}
              rowKey="id"
              pagination={{
                current: page,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} items`,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                },
              }}
            />
          </TabPane>

          <TabPane tab="Alert Rules" key="rules">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateRule}
              >
                New Rule
              </Button>
            </div>

            <Table
              columns={ruleColumns}
              dataSource={rules}
              loading={rulesLoading}
              rowKey="id"
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Alert Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedAlert && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{selectedAlert.id}</Descriptions.Item>
            <Descriptions.Item label="Type">{selectedAlert.alert_type}</Descriptions.Item>
            <Descriptions.Item label="Severity">
              <Tag color={getSeverityColor(selectedAlert.severity)}>
                {selectedAlert.severity.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedAlert.status)}>{selectedAlert.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Title">{selectedAlert.title}</Descriptions.Item>
            <Descriptions.Item label="Message">{selectedAlert.message}</Descriptions.Item>
            <Descriptions.Item label="Store ID">{selectedAlert.store_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email Sent">
              {selectedAlert.email_sent ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At (Brisbane)">
              {formatBrisbaneTime(selectedAlert.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Details">
              <pre style={{ maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify(selectedAlert.details, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={editingRule ? 'Edit Alert Rule' : 'New Alert Rule'}
        open={ruleModalVisible}
        onCancel={() => setRuleModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleRuleSubmit}>
          <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="alert_type" label="Alert Type" rules={[{ required: true }]}>
            <Select>
              <Option value="sync_failure">Sync Failure</Option>
              <Option value="sync_stalled">Sync Stalled</Option>
              <Option value="data_anomaly">Data Anomaly</Option>
              <Option value="rating_drop">Rating Drop</Option>
              <Option value="no_new_reviews">No New Reviews</Option>
              <Option value="api_error">API Error</Option>
              <Option value="system_error">System Error</Option>
            </Select>
          </Form.Item>
          <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
            <Select>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notify_email" label="Email Notification" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notify_slack" label="Slack Notification" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="cooldown_minutes" label="Cooldown (minutes)">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AlertsPage;
